import {cn} from '../lib/utils'
import {forwardRef, useImperativeHandle, useRef, useState} from 'react'

export interface InputFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  id: string
  label: string
  labelHidden?: boolean
  inputClassName?: string
  name: string
  error?: string
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  /**
   * Applied to the wrapper; the inner input and error message derive
   * `${data-testid}-input` / `${data-testid}-error`.
   */
  'data-testid': string
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      id,
      label,
      labelHidden,
      inputClassName,
      name,
      error,
      className,
      'data-testid': testId,
      onChange,
      onFocus,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    const [isFocused, setIsFocused] = useState(false)
    const [internalError, setInternalError] = useState<string | undefined>(undefined)

    const hasValue = props.value !== undefined ? String(props.value).length > 0 : false
    const isLabelFloating = isFocused || hasValue

    const validateInput = () => {
      const input = inputRef.current
      if (!input) return

      if (input.validity.valid) {
        setInternalError(undefined)
      } else {
        setInternalError(input.validationMessage)
      }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsFocused(true)
      if (internalError) {
        validateInput()
      }
      onChange?.(e)
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true)
      onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false)
      validateInput()
      onBlur?.(e)
    }

    const handleInvalid = (e: React.FormEvent<HTMLInputElement>) => {
      e.preventDefault()
      validateInput()
    }

    const displayError = error || internalError

    return (
      <div
        className={cn('flex w-full flex-col gap-8', className)}
        data-has-value={hasValue ? 'true' : 'false'}
        data-testid={testId}
      >
        <div className="input-group relative">
          <input
            ref={inputRef}
            className={cn(
              'w-full rounded-md border border-border bg-background px-[19px] py-[9px] text-base leading-base font-light text-text transition-colors outline-none placeholder:text-transparent',
              displayError &&
                'border-border-error hover:border-border-error focus:border-border-error',
              props.disabled && 'cursor-default border-border-disabled text-text-disabled',
              !props.disabled &&
                !displayError &&
                'hover:border-border-pressed focus:border-border-selected',
              inputClassName,
            )}
            id={id}
            name={name}
            data-testid={`${testId}-input`}
            onChange={(e) => handleChange(e)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onInvalid={handleInvalid}
            {...props}
          />
          {!labelHidden && (
            <label
              className={cn(
                'pointer-events-none absolute top-10 left-10 bg-background px-10 text-base leading-base font-light text-text-placeholder transition-all',
                isLabelFloating && '-top-8 text-xs leading-xs',
                displayError && 'text-text-error',
                props.disabled && 'text-text-disabled',
              )}
              htmlFor={id}
            >
              {label}
            </label>
          )}
        </div>
        {displayError && (
          <span
            className={cn(
              'text-xs leading-xs',
              displayError ? 'text-text-error' : 'text-text-secondary',
            )}
            data-testid={`${testId}-error`}
          >
            {displayError}
          </span>
        )}
      </div>
    )
  },
)

InputField.displayName = 'InputField'
